import { reportColumnAggregateTypesEnum } from "helpers/enums";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, OVERALL_REPORT_VAT_TOGGLE_COLUMNS } from "helpers/reports";
import React, { useMemo } from "react";
import { numbersList as trailersList } from "sources/books/trailers";
import { OverallReport } from "../overallReport";
import TrailerBids from "./trailerBids";

const trailerFamilyColumn = {
    label: "Семейство прицепа",
    propertyName: "TrailerFamilyTypeId",
    clrType: "int64",
    aggregateType: reportColumnAggregateTypesEnum.None,
};

const trailerTypeColumn = {
    label: "Тип прицепа",
    propertyName: "TrailerTypeName",
    clrType: "string",
    aggregateType: reportColumnAggregateTypesEnum.None,
};

const trailerSyntheticColumnNames = new Set([trailerFamilyColumn.propertyName, trailerTypeColumn.propertyName]);

class TrailersReport extends OverallReport {
    trailerFamilyFilterValue = (filters) =>
        filters.find((item) => item.id === columnName(trailerFamilyColumn))?.value?.value;

    setTableState = (data) => {
        const newTrailerFamilyValue = this.trailerFamilyFilterValue(data.filters);
        const oldTrailerFamilyValue = this.trailerFamilyFilterValue(this.state.filters);
        if (oldTrailerFamilyValue !== newTrailerFamilyValue) {
            this.props.getItem({
                trailerTypeFamilyTypeIds: newTrailerFamilyValue ? [newTrailerFamilyValue] : [],
            });
        }
        this.setState(data);
    };

    overallReportCellProps = this.cellProps;

    selectFilterProps = (valueKey, selectProps) => ({
        Filter: reportFilterWrapper((props) => (
            <SelectColumnFilter
                {...props}
                selectProps={{
                    ...selectProps,
                    inputProps: { name: valueKey },
                }}
            />
        )),
        filter: (rows, _id, filterValue) => {
            if (!filterValue?.value) {
                return rows;
            }
            return rows.filter((row) => row.original?.[valueKey] === filterValue.value);
        },
    });

    trailerFamilyFilterProps = () => ({
        Filter: reportFilterWrapper((props) => (
            <SelectColumnFilter
                {...props}
                selectProps={{
                    options: this.props.enums?.trailerFamilyType || [],
                    inputProps: { name: "trailerTypeFamilyTypeIds" },
                }}
            />
        )),
        filter: (rows) => rows,
    });

    cellProps = (column) => {
        const props = this.overallReportCellProps(column);
        if (column.propertyName === "Trailer") {
            return {
                ...props,
                ...this.selectFilterProps("trailerId", {
                    url: trailersList,
                    propName: "number",
                }),
            };
        }
        if (column.propertyName === trailerTypeColumn.propertyName) {
            return {
                ...props,
                ...this.selectFilterProps("trailerTypeId", {
                    options: this.props.enums?.trailerType || [],
                }),
            };
        }
        if (column.propertyName !== trailerFamilyColumn.propertyName) {
            return props;
        }

        return {
            ...props,
            ...this.trailerFamilyFilterProps(),
            Cell: ({ row }) => {
                const trailer = row.subRows?.[0]?.original || row.original;
                const familyName = trailer?.trailerFamilyTypeName || "Без семейства";
                const rowCount = row.subRows?.length ? ` (${row.subRows.length})` : "";
                return <span>{`${familyName}${rowCount}`}</span>;
            },
        };
    };

    extractColumns = () => {
        const { columns } = this.props.data || {};
        return columns.reduce(
            (reportColumns, column) => {
                if (column.label && !trailerSyntheticColumnNames.has(column.propertyName)) {
                    reportColumns.push(this.cellProps(column));
                }
                return reportColumns;
            },
            [this.cellProps(trailerFamilyColumn), this.cellProps(trailerTypeColumn)],
        );
    };

    tableExtraProps = () => {
        const { columns } = this.props.data || {};
        const hiddenColumns = columns
            .filter(
                (column) =>
                    column.isAdditionalColumn ||
                    column.isFooterData ||
                    column.propertyName === "CarLogistDisplayName" ||
                    (this.props.overallReportWithNds
                        ? OVERALL_REPORT_VAT_TOGGLE_COLUMNS.withoutNds.includes(column.propertyName)
                        : OVERALL_REPORT_VAT_TOGGLE_COLUMNS.withNds.includes(column.propertyName)),
            )
            .map(columnName);

        return {
            initialState: {
                groupBy: [columnName(trailerFamilyColumn)],
                hiddenColumns,
            },
            renderRowSubComponent: (row, isPrintForm) => <TrailerBids row={row} isPrintForm={isPrintForm} />,
        };
    };
}

/** Configure trailer-family aggregation without recalculating backend row values. */
export default function ConfiguredTrailersReport(props) {
    const reportData = props.data.data;
    const aggregatedData = useMemo(() => {
        return reportData.reduce((acc, item) => {
            const familyId = item.trailerFamilyTypeId;
            if (!acc[familyId]) {
                acc[familyId] = {
                    carHours: 0,
                    overallBidsPrice: 0,
                    overallBidsPriceWithoutVat: 0,
                    overallMileage: 0,
                };
            }
            acc[familyId].carHours += item.trailerHours;
            acc[familyId].overallBidsPrice += item.overallBidsPrice;
            acc[familyId].overallBidsPriceWithoutVat += item.overallBidsPriceWithoutVat;
            acc[familyId].overallMileage += item.overallMileage;
            return acc;
        }, {});
    }, [reportData]);

    return <TrailersReport {...props} aggregationKey="trailerFamilyTypeId" aggregatedData={aggregatedData} />;
}
